import { Archive, FilePenLine, Inbox, Plus, Send, Star, Trash2, Users } from "lucide-react";
import type { JSX } from "react";

import type { MailAccountDTO } from "../../../common/api/types.js";
import { SidebarItem, SidebarSection } from "../../../common/components/SidebarNav.js";
import type { ModulePageProps } from "../../ModuleDefinition.js";
import { useMailLocation } from "../mailLocation.js";
import { MAIL_VIEWS, type MailView } from "../mailTypes.js";
import { useMailAccounts } from "../query/mailQueries.js";

const ICONS: Record<MailView, typeof Inbox> = { inbox: Inbox, starred: Star, sent: Send, drafts: FilePenLine, archive: Archive, trash: Trash2 };

/** Eigenes Postfach und alle geteilten, jedes mit seinen Ordnern — wie in Outlook. */
export function MailSidebar(_props: ModulePageProps): JSX.Element {
  const accounts = useMailAccounts();
  const location = useMailLocation();
  const activeId = location.accountId ?? accounts.data?.[0]?.id ?? null;

  return (
    <div>
      <button type="button" disabled={!accounts.data?.length} onClick={() => location.openComposer()} className="mb-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-bold text-brand-ink shadow-sm hover:bg-brand/90 disabled:opacity-50">
        <Plus className="h-4 w-4" />Neue E-Mail
      </button>
      {accounts.isPending ? <p className="px-2 text-sm text-ink-muted">Postfächer werden geladen …</p> : null}
      {(accounts.data ?? []).map((account) => (
        <AccountSection key={account.id} account={account} active={account.id === activeId} view={location.folder} onOpen={(view) => location.openAccountFolder(account.id, view)} />
      ))}
    </div>
  );
}

function AccountSection({ account, active, view, onOpen }: { account: MailAccountDTO; active: boolean; view: MailView; onOpen: (view: MailView) => void }): JSX.Element {
  const folder = (role: MailView) => account.folders.find((entry) => entry.role === role);
  return (
    <SidebarSection title={account.personal ? account.address : `${account.address} · geteilt`} action={account.personal ? undefined : <Users className="h-3.5 w-3.5 text-ink-muted" aria-hidden />}>
      {MAIL_VIEWS.map(({ id, label }) => {
        const Icon = ICONS[id];
        const count = id === "inbox" ? folder("inbox")?.unread : id === "drafts" ? folder("drafts")?.total : undefined;
        return <SidebarItem key={id} label={label} icon={<Icon className="h-4 w-4" fill={id === "starred" && active && view === id ? "currentColor" : "none"} />} active={active && view === id} badge={count || undefined} onClick={() => onOpen(id)} />;
      })}
    </SidebarSection>
  );
}
